import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useMrrOverview } from '../hooks/useDashboard';
import type { MrrMovementItem, MrrMovementType, MrrOverview as MrrOverviewData } from '../api/dashboard';
import type { CustomerNames, OverrideMovementNote, OverrideSplit } from '../api/finance';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { InfoTip } from './ui/InfoTip';
import { EmptyState, ErrorState, LoadingState } from './ui/States';
import { MrrMonthDetailModal, OVERRIDE_KIND_LABEL, OverrideMovementLine, overrideNoteFor } from './MrrMonthDetail';
import { formatMoney, formatMoneyCompact } from '../utils/numberFormat';
import { CHART_AXIS, CHART_GRID, CHART_HEIGHT, CHART_SEMANTIC, CHART_SERIES } from './ui/chart';
import { formatMonthKey, inFinancePeriod, niceTicks } from '../pages/ProjectManagement/Finance/formatters';
import { ChartKey } from '../pages/ProjectManagement/Finance/ChartKey';

const SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

function formatCurrency(value: number, currency: string): string {
  return formatMoney(Math.round(value), SYMBOL[currency] ?? `${currency} `);
}

function formatCurrencyCompact(value: number, currency: string): string {
  return formatMoneyCompact(value, SYMBOL[currency] ?? `${currency} `);
}

/** "Sep 26" — never "Sept". */
const formatMonth = (dateStr: string) => formatMonthKey(dateStr);

const MRR_LINE = CHART_SERIES[0];
/** Both charts reserve the same Y-axis width so their months line up. */
const Y_AXIS_WIDTH = 56;
const CHART_MARGIN = { top: 4, right: 8, left: 8, bottom: 0 };

/**
 * Gains are the positive colour, losses the negative one; new vs expansion
 * (and churn vs contraction) differ only in strength, so the chart reads as
 * "up" and "down" first. The lighter pair stays at 0.7: at 0.5 a contraction
 * bar was a pale pink that barely showed against the white card.
 */
/**
 * `restored` is a reporting override running out (Xerox's reduced rate ending
 * in Oct 26): MRR moves back up to what Odoo's contracts always said.
 * `override` is one override starting, ending downwards or replacing another
 * (ISS North America's USD re-statement, Swiss GRC's tier steps). Neither is
 * new business, so each gets its own neutral stack instead of padding
 * expansion or contraction. The split itself comes from the API
 * (`overrideSplits`), so the tile, this card and the month detail agree.
 */
type MovementKind = MrrMovementType | 'restored' | 'override';

const MOVEMENT_STYLES: Record<MovementKind, { label: string; bar: string; opacity: number }> = {
  new: { label: 'New', bar: CHART_SEMANTIC.positive, opacity: 1 },
  expansion: { label: 'Expansion', bar: CHART_SEMANTIC.positive, opacity: 0.7 },
  restored: { label: 'Rate restored', bar: CHART_SEMANTIC.neutral, opacity: 1 },
  override: { label: 'Override changed', bar: CHART_SEMANTIC.neutral, opacity: 0.5 },
  contraction: { label: 'Contraction', bar: CHART_SEMANTIC.negative, opacity: 0.7 },
  churn: { label: 'Churned', bar: CHART_SEMANTIC.negative, opacity: 1 },
};

/**
 * Rate-restored movements per month, as ups and downs. Since the API classes
 * movements by `kind`, a restored one is never negative, so `down` is 0; kept
 * for Home's MRR tile, which reads it. New code reads `overrideSplits`.
 */
export function restoredByMonth(notes: OverrideMovementNote[] | undefined): Map<string, { up: number; down: number }> {
  const out = new Map<string, { up: number; down: number }>();
  for (const n of notes ?? []) {
    if (n.kind !== 'restored') continue;
    const entry = out.get(n.month) ?? { up: 0, down: 0 };
    const delta = n.to - n.from;
    if (delta >= 0) entry.up += delta;
    else entry.down += -delta;
    out.set(n.month, entry);
  }
  return out;
}

/** The month's engine movements, with the API's override split applied when it has one for that month. */
function applySplit<T extends { newMrr: number; expansionMrr: number; contractionMrr: number; churnedMrr: number }>(
  month: T,
  split: OverrideSplit | undefined,
): T & { restoredMrr: number; overrideChangedMrr: number } {
  if (!split) return { ...month, restoredMrr: 0, overrideChangedMrr: 0 };
  return {
    ...month,
    newMrr: split.newMrr,
    expansionMrr: split.expansionMrr,
    contractionMrr: split.contractionMrr,
    churnedMrr: split.churnedMrr,
    restoredMrr: split.restoredMrr,
    overrideChangedMrr: split.overrideChangedMrr,
  };
}

/** The display name for an engine customer: the Customers tab's name. */
function displayName(names: Record<string, CustomerNames> | undefined, customer: string): string {
  return names?.[customer]?.name ?? customer;
}

const deltaClass = (value: number) =>
  value > 0 ? 'text-success-dark' : value < 0 ? 'text-error-dark' : 'text-text-tertiary';

/** "+€3,000 new · +€3,750 expansion · +€4,833 rate restored · −€315 override changed" — the month's movements on one line. */
function MovementSummary({
  month,
  currency,
  split,
}: {
  month: MrrOverviewData['currentMonth'];
  currency: string;
  split?: OverrideSplit;
}) {
  const s = applySplit(month, split);
  const allParts: { type: MovementKind; value: number }[] = [
    { type: 'new', value: s.newMrr },
    { type: 'expansion', value: s.expansionMrr },
    ...(s.restoredMrr !== 0 ? [{ type: 'restored' as const, value: s.restoredMrr }] : []),
    ...(s.overrideChangedMrr !== 0 ? [{ type: 'override' as const, value: s.overrideChangedMrr }] : []),
    { type: 'contraction', value: -s.contractionMrr },
    { type: 'churn', value: -s.churnedMrr },
  ];
  const parts = allParts.filter((p) => p.value !== 0);
  const net = month.newMrr + month.expansionMrr - month.contractionMrr - month.churnedMrr;
  const overrideNet = s.restoredMrr + s.overrideChangedMrr;
  return (
    <div className="text-right">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-xs text-text-tertiary tabular-nums">
        {parts.length === 0 && <span>No MRR changes yet this month</span>}
        {parts.map((p, i) => (
          <span key={p.type} className="whitespace-nowrap">
            {i > 0 && <span className="mr-2 text-text-muted">·</span>}
            <span className={p.value !== 0 ? `font-medium ${deltaClass(p.value)}` : undefined}>
              {p.value > 0 ? '+' : ''}
              {formatCurrency(p.value, currency)}
            </span>{' '}
            {MOVEMENT_STYLES[p.type].label.toLowerCase()}
          </span>
        ))}
      </p>
      <p className="mt-0.5 text-body-xs text-text-tertiary tabular-nums">
        <span className={`font-medium ${deltaClass(net)}`}>
          {net > 0 ? '+' : ''}
          {formatCurrency(net, currency)}
        </span>{' '}
        net
        {s.restoredMrr !== 0 || s.overrideChangedMrr !== 0 ? (
          <>
            {/* The same phrase as Home's MRR tile: "new +€3,000 · sheet rates +€4,518" */}
            : new {net - overrideNet > 0 ? '+' : ''}
            {formatCurrency(net - overrideNet, currency)} · sheet rates {overrideNet > 0 ? '+' : ''}
            {formatCurrency(overrideNet, currency)}
          </>
        ) : null}
      </p>
    </div>
  );
}

function MovementsList({
  movements,
  currency,
  month,
  overrideNotes,
  customerNames,
}: {
  movements: MrrMovementItem[];
  currency: string;
  month: string;
  overrideNotes?: OverrideMovementNote[];
  customerNames?: Record<string, CustomerNames>;
}) {
  if (movements.length === 0) {
    return <p className="text-body-sm text-text-tertiary py-2">No MRR changes so far this month.</p>;
  }
  return (
    <div className="divide-y divide-border-light">
      {movements.map((m, idx) => {
        const note = overrideNoteFor(overrideNotes, month, m.companyName);
        return (
        <div key={idx} className="flex items-center justify-between gap-3 py-2 text-body-sm">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Badge>{note?.kind ? OVERRIDE_KIND_LABEL[note.kind] : MOVEMENT_STYLES[m.type].label}</Badge>
              <span className="min-w-0 break-words text-text-primary">
                {displayName(customerNames, m.companyName)}
              </span>
            </div>
            {note && <OverrideMovementLine note={note} currency={currency} />}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 tabular-nums">
            {(m.type === 'expansion' || m.type === 'contraction') && (
              <span className="text-body-xs text-text-tertiary">
                {formatCurrency(m.previousMrr, currency)} → {formatCurrency(m.currentMrr, currency)}
              </span>
            )}
            <span className={`font-medium ${deltaClass(m.delta)}`}>
              {m.delta > 0 ? '+' : ''}
              {formatCurrency(m.delta, currency)}
            </span>
          </div>
        </div>
        );
      })}
    </div>
  );
}

interface MrrTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: readonly { payload?: MrrChartPoint }[];
  currency: string;
  currentMonthKey: string;
}

/** One tooltip for both charts (they share a syncId), so hovering either shows the whole month. */
function MrrTooltip({ active, label, payload, currency, currentMonthKey }: MrrTooltipProps) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const rows: { name: string; value: number }[] = [
    { name: 'New', value: point.newMrr },
    { name: 'Expansion', value: point.expansionMrr },
    { name: 'Rate restored (override ended)', value: point.restoredMrr },
    { name: 'Override changed', value: point.overrideChangedMrr },
    { name: 'Contraction', value: -point.contractionMrr },
    { name: 'Churned', value: -point.churnedMrr },
  ];
  return (
    <div className="rounded border border-border-light bg-surface-primary px-3 py-2 text-body-xs shadow-medium tabular-nums">
      <p className="font-medium text-text-primary">{formatMonth(String(label))}</p>
      <p className="mt-1 text-text-primary">
        MRR <span className="font-semibold">{formatCurrency(point.mrr, currency)}</span>
        <span className={`ml-2 ${deltaClass(point.netChange)}`}>
          {point.netChange > 0 ? '+' : ''}
          {formatCurrency(point.netChange, currency)}
        </span>
      </p>
      <div className="mt-1 space-y-0.5 text-text-secondary">
        {rows
          .filter((r) => r.value !== 0)
          .map((r) => (
            <p key={r.name}>
              {r.name}: {r.value > 0 ? '+' : ''}
              {formatCurrency(r.value, currency)}
            </p>
          ))}
      </div>
      {point.date === currentMonthKey && (
        <p className="mt-1 text-text-muted">Month in progress; contracts ending this month churn next month</p>
      )}
      <p className="mt-1 text-text-muted">Click for the customer breakdown</p>
    </div>
  );
}

interface MrrChartPoint {
  date: string;
  mrr: number;
  newMrr: number;
  expansionMrr: number;
  contractionMrr: number;
  churnedMrr: number;
  netChange: number;
  restoredMrr: number;
  overrideChangedMrr: number;
  contractionNeg: number;
  churnedNeg: number;
}

/**
 * Total MRR and the monthly movements behind it, as two charts stacked on one
 * month axis: each has its own single Y axis, so the movement bars stand on a
 * real zero line instead of hanging from a second axis's midpoint. The headline
 * MRR/ARR figures are the page's KPI tiles, so this card does not repeat them.
 */
export function MrrOverview({
  overrideNotes,
  overrideSplits,
  customerNames,
}: {
  overrideNotes?: OverrideMovementNote[];
  overrideSplits?: Record<string, OverrideSplit>;
  customerNames?: Record<string, CustomerNames>;
} = {}) {
  const { data, isLoading, error, refetch } = useMrrOverview();
  const overview = data?.overview;
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const chartData: MrrChartPoint[] = useMemo(
    () =>
      // Finance data starts in Jan 2026; earlier months are not meaningful here.
      (overview?.history ?? []).filter((p) => inFinancePeriod(p.date)).map((p) => {
        const split = applySplit(p, overrideSplits?.[p.date]);
        return { ...split, contractionNeg: -split.contractionMrr, churnedNeg: -split.churnedMrr };
      }),
    [overview?.history, overrideSplits],
  );

  // MRR sits far from zero (€62k–€80k), so its axis starts near the data;
  // from €0 the line would flatten into the top quarter of the chart.
  const mrrScale = useMemo(
    () => niceTicks(chartData.map((p) => p.mrr), 4, { includeZero: false }),
    [chartData],
  );
  const movementScale = useMemo(
    () =>
      niceTicks(
        chartData.flatMap((p) => [
          p.newMrr + p.expansionMrr + Math.max(0, p.restoredMrr) + Math.max(0, p.overrideChangedMrr),
          p.contractionNeg + p.churnedNeg + Math.min(0, p.restoredMrr) + Math.min(0, p.overrideChangedMrr),
        ]),
        4,
      ),
    [chartData],
  );

  const currentMonthKey = chartData[chartData.length - 1]?.date ?? '';

  const handleBarClick = (entry: { payload?: { date?: string } }) => {
    if (entry?.payload?.date) setSelectedMonth(entry.payload.date);
  };
  // Clicking anywhere on a month column opens it, not only the thin bars.
  const handleChartClick = (state: { activeLabel?: string | number } | null) => {
    if (state?.activeLabel !== undefined) setSelectedMonth(String(state.activeLabel));
  };

  const currency = overview?.currency ?? 'EUR';
  const tooltip = (
    <Tooltip
      cursor={{ fill: 'rgb(18 18 18 / 0.04)', stroke: 'rgb(18 18 18 / 0.08)' }}
      content={(props) => <MrrTooltip {...props} currency={currency} currentMonthKey={currentMonthKey} />}
    />
  );

  return (
    <Card>
      <div className="mb-4 flex items-center gap-1.5">
        <h3 className="text-body-md font-medium text-text-primary">MRR by month</h3>
        <InfoTip>
          Top: total MRR at the end of each month. Bottom: that month&apos;s new, expansion, contraction and churned
          MRR. Rate restored is a reporting override from the MRR sheet running out (MRR goes back up to the Odoo
          contract amount); override changed is an override starting, changing or ending while the Odoo contracts
          stood still. Both are counted, but neither is new business. Click a month for the customer breakdown.
        </InfoTip>
      </div>

      {error ? (
        <ErrorState title="Couldn’t load MRR history" error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        // Two stacked charts plus their eyebrows: hold that height so the page doesn't jump.
        <LoadingState height={CHART_HEIGHT.small * 2 + 72} />
      ) : (
        <div className="space-y-6">
          {overview && chartData.length > 0 ? (
            <div>
              <p className="eyebrow mb-2">Total MRR</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
                <LineChart data={chartData} margin={CHART_MARGIN} syncId="mrr-by-month" onClick={handleChartClick}>
                  <CartesianGrid {...CHART_GRID} />
                  <XAxis dataKey="date" hide />
                  <YAxis
                    {...CHART_AXIS}
                    domain={mrrScale.domain}
                    ticks={mrrScale.ticks}
                    tickFormatter={(v) => formatCurrencyCompact(Number(v), currency)}
                    width={Y_AXIS_WIDTH}
                  />
                  {tooltip}
                  <Line
                    type="linear"
                    dataKey="mrr"
                    name="Total MRR"
                    stroke={MRR_LINE}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    activeDot={{ r: 4, cursor: 'pointer' }}
                  />
                </LineChart>
              </ResponsiveContainer>

              <p className="eyebrow mb-2 mt-6">Monthly changes</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
                <BarChart
                  data={chartData}
                  margin={CHART_MARGIN}
                  stackOffset="sign"
                  syncId="mrr-by-month"
                  onClick={handleChartClick}
                >
                  <CartesianGrid {...CHART_GRID} />
                  <XAxis dataKey="date" tickFormatter={formatMonth} {...CHART_AXIS} />
                  <YAxis
                    {...CHART_AXIS}
                    domain={movementScale.domain}
                    ticks={movementScale.ticks}
                    tickFormatter={(v) => formatCurrencyCompact(Number(v), currency)}
                    width={Y_AXIS_WIDTH}
                  />
                  {tooltip}
                  <ReferenceLine y={0} stroke={CHART_SEMANTIC.neutral} />
                  {(
                    [
                      ['newMrr', 'new'],
                      ['expansionMrr', 'expansion'],
                      ['restoredMrr', 'restored'],
                      ['overrideChangedMrr', 'override'],
                      ['contractionNeg', 'contraction'],
                      ['churnedNeg', 'churn'],
                    ] as const
                  ).map(([key, type]) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={MOVEMENT_STYLES[type].label}
                      stackId="movements"
                      fill={MOVEMENT_STYLES[type].bar}
                      fillOpacity={MOVEMENT_STYLES[type].opacity}
                      cursor="pointer"
                      onClick={handleBarClick}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <ChartKey
                items={(['new', 'expansion', 'restored', 'override', 'contraction', 'churn'] as const).map((type) => ({
                  label: MOVEMENT_STYLES[type].label,
                  color: MOVEMENT_STYLES[type].bar,
                  opacity: MOVEMENT_STYLES[type].opacity,
                }))}
              />
            </div>
          ) : (
            <EmptyState title="No MRR history yet" compact />
          )}

          {/* This month's movements: one summary line, then the customers behind it */}
          {overview && (
            <div className="border-t border-border-light pt-4">
              <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="text-body-sm font-medium text-text-secondary">Changes this month</p>
                <MovementSummary
                  month={overview.currentMonth}
                  currency={overview.currency}
                  split={overrideSplits?.[currentMonthKey]}
                />
              </div>
              <MovementsList
                movements={overview.currentMonth.movements}
                currency={overview.currency}
                month={currentMonthKey}
                overrideNotes={overrideNotes}
                customerNames={customerNames}
              />
            </div>
          )}
        </div>
      )}

      <MrrMonthDetailModal
        month={selectedMonth}
        onClose={() => setSelectedMonth(null)}
        overrideNotes={overrideNotes}
        customerNames={customerNames}
      />
    </Card>
  );
}
